import React, { useState } from "react";
import { X, Bug, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { submitProblemReport } from "@/lib/api";

/**
 * "Report a problem" modal. Opens from the sidebar pin (any view, any auth state).
 * Auto-attaches view name + URL + viewport + user-agent + app version to the
 * payload so we have triage context without asking the user.
 */
export default function ReportProblemModal({ open, onClose, currentView }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [doneId, setDoneId] = useState("");

  function reset() {
    setTitle(""); setDescription(""); setErr(""); setDoneId(""); setBusy(false);
  }

  function handleClose() {
    reset();
    onClose?.();
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (title.trim().length < 3) { setErr("Give it a short title (3+ characters)."); return; }
    if (description.trim().length < 10) { setErr("Add a bit more detail (10+ characters)."); return; }
    setBusy(true);
    try {
      const res = await submitProblemReport({
        title: title.trim(),
        description: description.trim(),
        view: currentView,
      });
      if (!res?.id) throw new Error("Empty response from server");
      setDoneId(res.id);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't submit right now. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose} data-testid="report-problem-overlay">
      <div
        className="modal-panel report-problem-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="report-problem-modal"
      >
        <div className="modal-header">
          <div className="report-problem-icon" aria-hidden="true">
            <Bug size={20} weight="duotone" />
          </div>
          <div>
            <div className="modal-title">Report a problem</div>
            <div className="modal-subtitle">
              {doneId ? "Thanks — we got it." : "Tell us what broke. We'll get a copy with debugging context."}
            </div>
          </div>
          <button
            className="icon-btn"
            onClick={handleClose}
            aria-label="Close"
            data-testid="report-problem-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="modal-body report-problem-body">
          {doneId ? (
            <div className="report-problem-success" data-testid="report-problem-success">
              <CheckCircle size={48} weight="duotone" />
              <h3>Report submitted</h3>
              <p>
                We logged it as <code>{doneId}</code> and emailed the team. If you included a way to reach you
                in the description, we'll follow up. Otherwise, sit tight — fixes usually ship within the week.
              </p>
              <button
                type="button"
                className="btn btn--accent"
                onClick={handleClose}
                data-testid="report-problem-done"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="report-problem-form">
              <label className="field">
                <span className="field-label">SHORT TITLE</span>
                <input
                  type="text"
                  className="field-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Save button does nothing on Symptom Tracker"
                  maxLength={140}
                  required
                  autoFocus
                  data-testid="report-problem-title"
                />
                <span className="field-hint">{title.length}/140</span>
              </label>

              <label className="field">
                <span className="field-label">WHAT HAPPENED?</span>
                <textarea
                  className="field-input field-input--textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Walk us through it: what did you click, what did you expect, what happened instead? If it's a question instead of a bug, ask away."
                  rows={7}
                  maxLength={4000}
                  required
                  data-testid="report-problem-description"
                />
                <span className="field-hint">{description.length}/4000</span>
              </label>

              <div className="report-problem-context" data-testid="report-problem-context">
                <div className="report-problem-context-label">Auto-attached</div>
                <ul className="report-problem-context-list">
                  <li>View: <strong>{currentView || "n/a"}</strong></li>
                  <li>Page URL + browser + viewport size</li>
                  <li>Your email (if signed in)</li>
                </ul>
              </div>

              {err && (
                <div className="auth-prompt-error" data-testid="report-problem-error">
                  {err}
                </div>
              )}

              <div className="report-problem-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={handleClose}
                  data-testid="report-problem-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--accent"
                  disabled={busy}
                  data-testid="report-problem-submit"
                >
                  <PaperPlaneTilt size={14} weight="bold" />
                  {busy ? "Sending..." : "Send report"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
